// Demo/localStorage data layer for Incident Reports. Mirrors live-mode shape.

const STORE_KEY = "daycare.demo.incidents.v1";
const ORG_ID = "00000000-0000-0000-0000-000000000001";
const SUNFLOWER_ID = "00000000-0000-0000-0000-000000000101";
const NOA_ID = "00000000-0000-0000-0000-000000000301";

function hoursAgo(n) {
  return new Date(Date.now() - n * 60 * 60 * 1000).toISOString();
}

function seedInitial() {
  const incidents = [
    {
      id: "demo-incident-1",
      organization_id: ORG_ID,
      child_id: NOA_ID,
      classroom_id: SUNFLOWER_ID,
      incident_type: "injury",
      severity: "low",
      occurred_at: hoursAgo(28),
      location: "Outdoor play area",
      description:
        "Noa tripped while running on the grass and scraped her left knee. No loss of consciousness.",
      action_taken:
        "Cleaned the scrape with soap and water, applied bandage. Noa was comforted and returned to play after a few minutes.",
      parent_notified_at: hoursAgo(27),
      parent_notification_method: "message",
      status: "submitted",
      reported_by: "demo-teacher",
      acknowledged_by: null,
      acknowledged_at: null,
      resolved_by: null,
      resolved_at: null,
      resolution_note: null,
      created_at: hoursAgo(27),
      updated_at: hoursAgo(27),
    },
  ];
  return { incidents };
}

function readStore() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // fall through
  }
  const seeded = seedInitial();
  writeStore(seeded);
  return seeded;
}
function writeStore(store) {
  localStorage.setItem(STORE_KEY, JSON.stringify(store));
}

// -------------------------------------------------------------------------
// Public demo API
// -------------------------------------------------------------------------

/** @param {string[]} childIds restrict to these children (parents). */
export function demoListIncidents({ role, profileId, childIds, filters = {} } = {}) {
  const store = readStore();
  let rows = store.incidents;

  if (role === "parent" && childIds) {
    const allowed = new Set(childIds);
    rows = rows.filter((r) => allowed.has(r.child_id));
  } else if (role === "teacher") {
    // Demo teacher covers Sunflower only.
    rows = rows.filter((r) => r.classroom_id === SUNFLOWER_ID);
  } else if (role === "cms_editor") {
    return [];
  }

  if (filters.classroomId) rows = rows.filter((r) => r.classroom_id === filters.classroomId);
  if (filters.childId)     rows = rows.filter((r) => r.child_id === filters.childId);
  if (filters.severity)    rows = rows.filter((r) => r.severity === filters.severity);
  if (filters.status)      rows = rows.filter((r) => r.status === filters.status);
  if (filters.dateFrom)    rows = rows.filter((r) => r.occurred_at.slice(0, 10) >= filters.dateFrom);
  if (filters.dateTo)      rows = rows.filter((r) => r.occurred_at.slice(0, 10) <= filters.dateTo);

  // Teacher mode also gets "only mine" option via profileId (not currently enforced here
  // because the demo teacher is the sole reporter).
  void profileId;

  return rows
    .slice()
    .sort((a, b) => (b.occurred_at || "").localeCompare(a.occurred_at || ""));
}

export function demoGetIncident(id) {
  const store = readStore();
  return store.incidents.find((r) => r.id === id) || null;
}

export function demoCreateIncident(payload) {
  const store = readStore();
  const now = new Date().toISOString();
  const row = {
    id: `demo-incident-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    organization_id: ORG_ID,
    child_id: payload.childId,
    classroom_id: payload.classroomId,
    incident_type: payload.incidentType,
    severity: payload.severity,
    occurred_at: payload.occurredAt,
    location: payload.location || null,
    description: payload.description,
    action_taken: payload.actionTaken,
    parent_notified_at: payload.parentNotifiedAt || null,
    parent_notification_method: payload.parentNotificationMethod || null,
    status: "submitted",
    reported_by: payload.reportedBy,
    acknowledged_by: null,
    acknowledged_at: null,
    resolved_by: null,
    resolved_at: null,
    resolution_note: null,
    created_at: now,
    updated_at: now,
  };
  store.incidents.push(row);
  writeStore(store);
  return row;
}

export function demoAcknowledgeIncident({ incidentId, profileId }) {
  const store = readStore();
  const now = new Date().toISOString();
  store.incidents = store.incidents.map((r) =>
    r.id === incidentId && !r.acknowledged_at
      ? {
          ...r,
          acknowledged_by: profileId,
          acknowledged_at: now,
          status: r.status === "submitted" ? "acknowledged" : r.status,
          updated_at: now,
        }
      : r,
  );
  writeStore(store);
  return store.incidents.find((r) => r.id === incidentId);
}

export function demoResolveIncident({ incidentId, profileId, note }) {
  const store = readStore();
  const now = new Date().toISOString();
  store.incidents = store.incidents.map((r) =>
    r.id === incidentId
      ? {
          ...r,
          resolved_by: profileId,
          resolved_at: now,
          resolution_note: note || r.resolution_note,
          status: "resolved",
          updated_at: now,
        }
      : r,
  );
  writeStore(store);
  return store.incidents.find((r) => r.id === incidentId);
}
